import { type TikTokCreatorInfo, normalizeTikTokCreatorInfo } from '@shared/tiktok-creator-info';

import { BaseService } from '../base/BaseService';
import type { ServiceResponse } from '../base/ServiceTypes';

export type TikTokCreatorInfoState = 'fresh' | 'stale' | 'unavailable' | 'not_connected';

export interface TikTokCreatorInfoResult {
  state: TikTokCreatorInfoState;
  info: TikTokCreatorInfo | null;
  connectionId: string | null;
}

class TikTokCreatorInfoService extends BaseService {
  private static instance: TikTokCreatorInfoService;

  private constructor() {
    super({ defaultTTL: 0, enableCache: false, enableRetry: false });
  }

  static getInstance(): TikTokCreatorInfoService {
    if (!TikTokCreatorInfoService.instance) {
      TikTokCreatorInfoService.instance = new TikTokCreatorInfoService();
    }
    return TikTokCreatorInfoService.instance;
  }

  /** Asks TikTok (through the edge function) for the account's current posting options. */
  async refresh(connectionId?: string): Promise<ServiceResponse<TikTokCreatorInfoResult>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data, error } = await this.client.functions.invoke<Record<string, unknown>>(
        'tiktok-creator-info',
        { body: connectionId ? { connectionId } : {} },
      );
      if (error) throw error;
      const state = data?.state;
      if (
        state !== 'fresh' &&
        state !== 'stale' &&
        state !== 'unavailable' &&
        state !== 'not_connected'
      ) {
        throw new Error('TikTok creator info returned an unexpected response');
      }
      return {
        state,
        info: normalizeTikTokCreatorInfo(data?.info),
        connectionId: typeof data?.connectionId === 'string' ? data.connectionId : null,
      };
    }, 'Failed to check the TikTok account');
  }
}

export const tikTokCreatorInfoService = TikTokCreatorInfoService.getInstance();
